import { parseAsJson, useQueryState } from 'nuqs'
import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { SortOrder, useSortTable } from '@/hooks/useSortTable'
import { useAgentParams } from '@/models/agent'
import {
  IndicationsAgentResponse,
  PersonalAgentDataResponse,
  TypesIndicationsResponse,
} from '@/models/backoffice/indications'
import {
  indicationFiltersFormValidationSchema,
  IndicationFiltersFormValues,
} from '@/schemas/backoffice/indications-schema'
import api from '@/services/api'
import { getIndicationsByAgentId } from '@/utils/indications'

interface IndicationsContextType {
  personalAgentData: PersonalAgentDataResponse | null
  indicationsAgent: IndicationsAgentResponse[] | null
  indicationsType: TypesIndicationsResponse[] | null

  setPersonalAgentData: React.Dispatch<
    React.SetStateAction<PersonalAgentDataResponse | null>
  >
  setIndicationsAgent: React.Dispatch<
    React.SetStateAction<IndicationsAgentResponse[] | null>
  >

  setIndicationsType: React.Dispatch<
    React.SetStateAction<TypesIndicationsResponse[] | null>
  >
  agentId: string

  handleUpdateNumberOfSponsorsOrProperties(
    isSum: boolean,
    isSelectedProperty?: boolean,
  ): void

  sortQueryString: string
  toggleSortOrder: (column: keyof IndicationsAgentResponse) => void
  getSortOrder: (column: keyof IndicationsAgentResponse) => SortOrder
  handleWithTheUpdateOfIndicationFiltersValue(
    value: IndicationFiltersFormValues,
  ): void

  indicationsFilters: IndicationFiltersFormValues | null
  isLoadingIndications: boolean
}

export const IndicationsContext = createContext<IndicationsContextType>(
  {} as IndicationsContextType,
)

export function IndicationsProvider({ children }: { children: ReactNode }) {
  const { agentId } = useParams<useAgentParams>()

  const { sortQueryString, toggleSortOrder, getSortOrder } =
    useSortTable<keyof IndicationsAgentResponse>()

  const [personalAgentData, setPersonalAgentData] =
    useState<PersonalAgentDataResponse | null>(null)

  const [indicationsAgent, setIndicationsAgent] = useState<
    IndicationsAgentResponse[] | null
  >(null)

  const [indicationsType, setIndicationsType] = useState<
    TypesIndicationsResponse[] | null
  >(null)

  const [indicationsFilters, setIndicationFilters] = useQueryState(
    'filters',
    parseAsJson(indicationFiltersFormValidationSchema.parse),
  )

  const [isLoadingIndications, setIsLoadingIndications] =
    useState<boolean>(false)

  const getPersonalAgentData = useCallback(() => {
    async function getData() {
      await api
        .get<PersonalAgentDataResponse>(`/agent/info/${agentId}`)
        .then(({ data }) => setPersonalAgentData(data))
        .catch((error) => toast.error(error.response.data.message))
    }
    getData()
  }, [agentId, setPersonalAgentData])

  const getTypeOfIndications = useCallback(() => {
    async function getType() {
      await api
        .get<TypesIndicationsResponse[]>('/agent/referral/types')
        .then(({ data }) => setIndicationsType(data))
        .catch((error) => toast.error(error.response.data.message))
    }
    getType()
  }, [setIndicationsType])

  useEffect(() => {
    Promise.all([getPersonalAgentData(), getTypeOfIndications()])
  }, [getTypeOfIndications, getPersonalAgentData])

  const getIndicationsFromAgentById = useCallback(async () => {
    setIsLoadingIndications(true)
    await getIndicationsByAgentId({
      agentId: agentId ?? '',
      params: sortQueryString,
      ...(indicationsFilters && {
        queryParam: {
          referralStatus: indicationsFilters.indications_status,
          referralType: indicationsFilters.indications_type,
          resourceName: indicationsFilters.indications_search,
        },
      }),
    })
      .then(({ data }) => setIndicationsAgent(data))
      .catch((error) => toast.error(error.response.data.message))
      .finally(() => setIsLoadingIndications(false))
  }, [agentId, indicationsFilters, sortQueryString])

  useEffect(() => {
    getIndicationsFromAgentById()
  }, [getIndicationsFromAgentById])

  function handleUpdateNumberOfSponsorsOrProperties(
    isSum: boolean,
    isSelectedProperty: boolean,
  ) {
    setPersonalAgentData((prevState) => {
      if (!prevState) return prevState

      if (isSum) {
        return {
          ...prevState,
          total_indicacoes: prevState.total_indicacoes + 1,
          ...(isSelectedProperty
            ? { propriedades: prevState.propriedades + 1 }
            : { patrocinadores: prevState.patrocinadores + 1 }),
        }
      }

      return {
        ...prevState,
        total_indicacoes: prevState.total_indicacoes - 1,
        ...(isSelectedProperty
          ? { propriedades: prevState.propriedades - 1 }
          : { patrocinadores: prevState.patrocinadores - 1 }),
      }
    })
  }

  function handleWithTheUpdateOfIndicationFiltersValue(
    data: IndicationFiltersFormValues,
  ) {
    const doesIndicationFiltersIsEmpty = Object.values(data).every((value) => {
      return value === ''
    })
    setIndicationFilters(doesIndicationFiltersIsEmpty ? null : data)
  }

  return (
    <IndicationsContext.Provider
      value={{
        isLoadingIndications,
        indicationsFilters,
        handleWithTheUpdateOfIndicationFiltersValue,
        sortQueryString,
        getSortOrder,
        toggleSortOrder,
        handleUpdateNumberOfSponsorsOrProperties,
        agentId: agentId ?? '',
        indicationsAgent,
        indicationsType,
        personalAgentData,
        setIndicationsAgent,
        setIndicationsType,
        setPersonalAgentData,
      }}
    >
      {children}
    </IndicationsContext.Provider>
  )
}
